import React, { useContext, useEffect, useState } from 'react';
import { BsBell, BsPerson, BsBoxArrowRight } from 'react-icons/bs';
import { jwtDecode } from 'jwt-decode';
import { getCompanyDetails, getAllNotifications, logoutUser, removeStoredTokens } from '../../API/Api';
import { DateTime } from 'luxon';
import { LuBellDot } from 'react-icons/lu';
import BasicMenu from './BasicMenu';
import { Link } from 'react-router-dom';
import { TokenContext } from '../../App';

const TopBar = ({ setIsLoggedIn }) => {
  const [allNotifications, setAllNotifications] = useState([]);
  const [image, setImage] = useState();
  const [companyData, setCompanyData] = useState(null);
  const [remainingDays, setRemainingDays] = useState(0);

  let [encodedAccessToken] = useContext(TokenContext);
  const decodedToken = jwtDecode(encodedAccessToken);
  const [companyName, setCompanyName] = useState();

  // Calculate remaining trial days
  const calculateTrialDays = (createDate, trialDuration) => {
    if (!createDate || !trialDuration) return 0;

    try {
      const startDate = DateTime.fromISO(createDate);
      if (!startDate.isValid) return 0;

      const endDate = startDate.plus({ days: parseInt(trialDuration) });
      const now = DateTime.now();
      const remaining = endDate.diff(now, 'days').days;

      return Math.max(0, Math.ceil(remaining));
    } catch (error) {
      return 0;
    }
  };

  useEffect(() => {
    const fetchCompanyLogo = async () => {
      try {
        const companyData = await getCompanyDetails('company');
        setCompanyData(companyData?.data);
        setCompanyName(companyData?.data?.companyName);

        if (companyData?.data?.logoUrl) {
          setImage(companyData?.data?.logoUrl);
        }

        // Calculate trial days only if trial is active
        if (companyData?.data?.trial && companyData?.data?.createDate && companyData?.data?.trialDuration) {
          const days = calculateTrialDays(companyData.data.createDate, companyData.data.trialDuration);
          setRemainingDays(days);
        }

        await new Promise(r => setTimeout(r, 500));

        const notifications = await getAllNotifications('');
        setAllNotifications(notifications?.data);
      } catch (error) {
        console.error('Error fetching company data:', error);
      }
    };

    if (decodedToken) {
      fetchCompanyLogo();
    }
  }, []);

  function getRelativeTimeString(timestamp) {
    const pastDateTime = DateTime.fromFormat(timestamp, 'dd-MM-yyyy HH:mm:ss'); // Parse the given timestamp
    const now = DateTime.now();
    const diff = now.diff(pastDateTime); // Calculate the difference

    const days = diff.as('days'); // Convert to units
    const hours = diff.as('hours');
    const minutes = diff.as('minutes');

    if (days >= 1) {
      return `${Math.floor(days)} day${days >= 2 ? 's' : ''} ago`;
    } else if (hours >= 1) {
      return `${Math.floor(hours)} hour${hours >= 2 ? 's' : ''} ago`;
    } else if (minutes >= 1) {
      return `${Math.floor(minutes)} minute${minutes >= 2 ? 's' : ''} ago`;
    } else {
      return 'Just now';
    }
  }

  async function handleLogOut() {
    await logoutUser();
    removeStoredTokens();
    localStorage.removeItem('stringToken');
    window.location = '/login';
  }

  // Render trial info - memoized to prevent unnecessary re-renders
  const renderTrialInfo = React.useMemo(() => {
    if (!companyData?.trial) return null;

    return (
      <div className='flex items-center ml-2'>
        <span className='bg-yellow-500 text-white text-xs font-bold px-2 py-1 rounded-full mr-2'>Trial</span>
        <span className='text-white text-sm font-medium'>{remainingDays} days left</span>
      </div>
    );
  }, [companyData?.trial, remainingDays]);

  return (
    <nav className='bg-green-500 z-10 p-1.5 sticky top-0 '>
      <div className='flex justify-end items-center'>
        <div className='flex items-center'>
          {image ? (
            <img src={image} alt='Profile' className='w-10 h-8 object-cover bg-white float-right' />
          ) : (
            <span className='font-serif border-y border-green-300 px-2 '> {companyName} </span>
          )}
          {renderTrialInfo}
        </div>

        <BasicMenu
          icon={
            true ? (
              <BsBell className='text-white scale-150 mx-3' />
            ) : (
              <LuBellDot className='scale-150 text-red-500 mx-3' />
            )
          }
        >
          <div className='bottom-2 relative'>
            <p className='px-4 py-2 bg-cyan-900 sticky top-0 border-2 border-cyan-900 text-white rounded-t'>
              Notifications
            </p>
            <ul>
              {allNotifications.map((notification, index) => (
                <li key={index} className='px-4 py-2 hover:bg-green-100 hover:rounded-lg'>
                  <div className='flex justify-between'>
                    <h4 className='font-bold mb-2'> {notification.type} </h4>
                    <p className='text-sm text-gray-800'> {getRelativeTimeString(notification.date)} </p>
                  </div>
                  <p> {notification.text} </p>
                </li>
              ))}
            </ul>
          </div>
        </BasicMenu>

        <BasicMenu icon={<BsPerson className='scale-150 text-white mr-5' />}>
          <Link to='/profile' className='text-black px-4 py-2 hover:bg-gray-200 hover:rounded-lg flex justify-between'>
            {' '}
            User Profile <BsPerson className='scale-125 inline ml-3' />{' '}
          </Link>
          <span
            href='/login'
            onClick={handleLogOut}
            className='text-black px-4 py-2 hover:bg-gray-200 hover:rounded-lg flex justify-between cursor-pointer'
          >
            {' '}
            Logout <BsBoxArrowRight className='scale-125 inline' />{' '}
          </span>
        </BasicMenu>
      </div>
    </nav>
  );
};

export default TopBar;
